import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Checkbox } from "../components/ui/checkbox";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { 
  ArrowLeft, Save, Upload, Link as LinkIcon, Trash2, Download,
  AlertTriangle, Clock, CheckCircle2, XCircle, FileText, 
  User, Calendar, Building, ExternalLink
} from "lucide-react";
import Layout from "../components/Layout";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const ESTATUS_OPTIONS = [
  { value: "Por preparar", label: "Por preparar" },
  { value: "Enviado a dueño", label: "Enviado a dueño" },
  { value: "Firmado por dueño", label: "Firmado por dueño" },
  { value: "Enviado a inquilino/fiador", label: "Enviado a inquilino/fiador" },
  { value: "Recibido firmado", label: "Recibido firmado" },
  { value: "Validado y archivado", label: "Validado y archivado" },
  { value: "Cancelado", label: "Cancelado" },
];

const EN_MANOS_OPTIONS = [
  { value: "Oficina", label: "Oficina" },
  { value: "Dueño", label: "Dueño" },
  { value: "Inquilino", label: "Inquilino" },
  { value: "Fiador", label: "Fiador" },
];

const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  try {
    return new Date(dateStr).toLocaleDateString('es-MX', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  } catch {
    return dateStr;
  }
};

const formatDateTime = (dateStr) => {
  if (!dateStr) return "-";
  try {
    return new Date(dateStr).toLocaleString('es-MX', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch {
    return dateStr;
  }
};

export default function RenovacionDetail({ user, onLogout }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  const [renovacion, setRenovacion] = useState(null);
  const [bitacora, setBitacora] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    estatus: "",
    en_manos_de: "",
    fecha_limite: "",
    responsable_interno: "",
    observaciones: "",
    firma_dueno: false,
    firma_inquilino: false,
    firma_fiador: false,
    nota_bitacora: ""
  });
  
  // Link modal
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [newLink, setNewLink] = useState({ nombre: "", url: "" });

  useEffect(() => {
    fetchRenovacion();
  }, [id]);

  const fetchRenovacion = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/renovaciones/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const ren = response.data.renovacion;
      setRenovacion(ren);
      setBitacora(response.data.bitacora);
      
      setFormData({
        estatus: ren.estatus,
        en_manos_de: ren.en_manos_de,
        fecha_limite: ren.fecha_limite || "",
        responsable_interno: ren.responsable_interno || "",
        observaciones: ren.observaciones || "",
        firma_dueno: ren.firma_dueno,
        firma_inquilino: ren.firma_inquilino,
        firma_fiador: ren.firma_fiador,
        nota_bitacora: ""
      });
    } catch (error) {
      toast.error("Error al cargar renovación");
      navigate("/renovaciones");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      await axios.put(`${API}/renovaciones/${id}`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Cambios guardados");
      setFormData(prev => ({ ...prev, nota_bitacora: "" }));
      fetchRenovacion();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al guardar cambios");
    } finally {
      setSaving(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const formDataFile = new FormData();
    formDataFile.append("file", file);
    
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/renovaciones/${id}/archivos`, formDataFile, {
        headers: { 
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data"
        }
      });
      
      toast.success("Archivo subido");
      fetchRenovacion();
    } catch (error) {
      toast.error("Error al subir archivo");
    }
    
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleAddLink = async () => {
    if (!newLink.nombre || !newLink.url) {
      toast.error("Completa nombre y URL");
      return;
    }
    
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/renovaciones/${id}/links`, newLink, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Link agregado");
      setShowLinkModal(false);
      setNewLink({ nombre: "", url: "" });
      fetchRenovacion();
    } catch (error) {
      toast.error("Error al agregar link");
    }
  };

  const handleDeleteArchivo = async (archivoId) => {
    if (!window.confirm("¿Eliminar este archivo/link?")) return;
    
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/renovaciones/${id}/archivos/${archivoId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Archivo eliminado");
      fetchRenovacion();
    } catch (error) {
      toast.error("Error al eliminar archivo");
    }
  };

  const handleDownloadArchivo = async (archivo) => {
    if (archivo.tipo === "link") {
      window.open(archivo.url, "_blank");
      return;
    }
    
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${API}/renovaciones/${id}/archivos/${archivo.id}/download`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: "blob"
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", archivo.nombre);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error("Error al descargar archivo");
    }
  };

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="flex items-center justify-center h-64">
          <div className="text-lg">Cargando...</div>
        </div>
      </Layout>
    );
  }

  if (!renovacion) return null;

  const hasChanges = 
    formData.estatus !== renovacion.estatus ||
    formData.en_manos_de !== renovacion.en_manos_de ||
    formData.fecha_limite !== (renovacion.fecha_limite || "") ||
    formData.responsable_interno !== (renovacion.responsable_interno || "") ||
    formData.observaciones !== (renovacion.observaciones || "") ||
    formData.firma_dueno !== renovacion.firma_dueno ||
    formData.firma_inquilino !== renovacion.firma_inquilino ||
    formData.firma_fiador !== renovacion.firma_fiador;

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-6 fade-in max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate("/renovaciones")}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Volver
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{renovacion.folio}</h1>
              <p className="text-gray-600">{renovacion.inmueble_nombre}</p>
            </div>
          </div>
          
          <Button onClick={handleSave} disabled={saving || !hasChanges}>
            <Save className="w-4 h-4 mr-2" />
            {saving ? "Guardando..." : "Guardar Cambios"}
          </Button>
        </div>

        {/* Alerts */}
        {renovacion.vencida && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-red-500" />
            <span className="text-red-700 font-medium">
              Esta renovación está vencida (fecha límite: {formatDate(renovacion.fecha_limite)})
            </span>
          </div>
        )}
        
        {renovacion.dias_en_estatus > 7 && !["Validado y archivado", "Cancelado"].includes(renovacion.estatus) && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-center gap-3">
            <Clock className="w-5 h-5 text-amber-500" />
            <span className="text-amber-700 font-medium">
              Esta renovación lleva {renovacion.dias_en_estatus} días en el mismo estatus
            </span>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Main Info */}
          <div className="lg:col-span-2 space-y-6">
            {/* Basic Info */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Información del Contrato
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-500">Contrato</p>
                    <p className="font-medium">{renovacion.contrato_numero}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Inmueble</p>
                    <p className="font-medium">{renovacion.inmueble_nombre}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Inquilino</p>
                    <p className="font-medium">{renovacion.inquilino_nombre}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Propietario</p>
                    <p className="font-medium">{renovacion.propietario_nombre}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Tiene Fiador</p>
                    <p className="font-medium">{renovacion.tiene_fiador ? "Sí" : "No"}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Fecha de Creación</p>
                    <p className="font-medium">{formatDate(renovacion.fecha_creacion)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Control */}
            <Card>
              <CardHeader>
                <CardTitle>Control de Estatus</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Estatus</Label>
                    <Select 
                      value={formData.estatus} 
                      onValueChange={(v) => setFormData({...formData, estatus: v})}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {ESTATUS_OPTIONS.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label>En manos de</Label>
                    <Select 
                      value={formData.en_manos_de} 
                      onValueChange={(v) => setFormData({...formData, en_manos_de: v})}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {EN_MANOS_OPTIONS.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label>Fecha límite</Label>
                    <Input
                      type="date"
                      value={formData.fecha_limite}
                      onChange={(e) => setFormData({...formData, fecha_limite: e.target.value})}
                    />
                  </div>
                  
                  <div>
                    <Label>Responsable interno</Label>
                    <Input
                      value={formData.responsable_interno}
                      onChange={(e) => setFormData({...formData, responsable_interno: e.target.value})}
                      placeholder="Nombre..."
                    />
                  </div>
                </div>
                
                <div>
                  <Label>Observaciones</Label>
                  <Textarea
                    value={formData.observaciones}
                    onChange={(e) => setFormData({...formData, observaciones: e.target.value})}
                    placeholder="Notas adicionales..."
                    rows={3}
                  />
                </div>
                
                <div>
                  <Label>Nota para bitácora (opcional)</Label>
                  <Input
                    value={formData.nota_bitacora}
                    onChange={(e) => setFormData({...formData, nota_bitacora: e.target.value})}
                    placeholder="Agregar nota al registro de cambios..."
                  />
                </div>
              </CardContent>
            </Card>

            {/* Signatures Checklist */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5" />
                  Checklist de Firmas
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <label className="flex items-center gap-3 p-3 rounded-lg border hover:bg-gray-50 cursor-pointer">
                    <Checkbox
                      checked={formData.firma_dueno}
                      onCheckedChange={(checked) => setFormData({...formData, firma_dueno: checked})}
                    />
                    <div className="flex-1">
                      <p className="font-medium">Firma Dueño</p>
                      <p className="text-sm text-gray-500">{renovacion.propietario_nombre}</p>
                    </div>
                    {formData.firma_dueno ? (
                      <CheckCircle2 className="w-5 h-5 text-green-500" />
                    ) : (
                      <XCircle className="w-5 h-5 text-gray-300" />
                    )}
                  </label>
                  
                  <label className="flex items-center gap-3 p-3 rounded-lg border hover:bg-gray-50 cursor-pointer">
                    <Checkbox
                      checked={formData.firma_inquilino}
                      onCheckedChange={(checked) => setFormData({...formData, firma_inquilino: checked})}
                    />
                    <div className="flex-1">
                      <p className="font-medium">Firma Inquilino</p>
                      <p className="text-sm text-gray-500">{renovacion.inquilino_nombre}</p>
                    </div>
                    {formData.firma_inquilino ? (
                      <CheckCircle2 className="w-5 h-5 text-green-500" />
                    ) : (
                      <XCircle className="w-5 h-5 text-gray-300" />
                    )}
                  </label>
                  
                  {renovacion.tiene_fiador && (
                    <label className="flex items-center gap-3 p-3 rounded-lg border hover:bg-gray-50 cursor-pointer">
                      <Checkbox
                        checked={formData.firma_fiador}
                        onCheckedChange={(checked) => setFormData({...formData, firma_fiador: checked})}
                      />
                      <div className="flex-1">
                        <p className="font-medium">Firma Fiador</p>
                        <p className="text-sm text-gray-500">Requerido</p>
                      </div>
                      {formData.firma_fiador ? (
                        <CheckCircle2 className="w-5 h-5 text-green-500" />
                      ) : (
                        <XCircle className="w-5 h-5 text-gray-300" />
                      )}
                    </label>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Files and Links */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span>Archivos y Links</span>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()}>
                      <Upload className="w-4 h-4 mr-1" />
                      Subir
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => setShowLinkModal(true)}>
                      <LinkIcon className="w-4 h-4 mr-1" />
                      Agregar Link
                    </Button>
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <input
                  type="file"
                  ref={fileInputRef}
                  className="hidden"
                  onChange={handleFileUpload}
                />
                
                {renovacion.archivos?.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">No hay archivos o links</p>
                ) : (
                  <div className="space-y-2">
                    {renovacion.archivos?.map((archivo) => (
                      <div key={archivo.id} className="flex items-center gap-3 p-3 rounded-lg border hover:bg-gray-50">
                        {archivo.tipo === "archivo" ? (
                          <FileText className="w-5 h-5 text-blue-500" />
                        ) : (
                          <ExternalLink className="w-5 h-5 text-purple-500" />
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate">{archivo.nombre}</p>
                          <p className="text-xs text-gray-500">
                            {archivo.tipo === "archivo" ? "Archivo" : "Link"} • {formatDate(archivo.fecha_subida)}
                          </p>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDownloadArchivo(archivo)}
                        >
                          {archivo.tipo === "archivo" ? (
                            <Download className="w-4 h-4" />
                          ) : (
                            <ExternalLink className="w-4 h-4" />
                          )}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteArchivo(archivo.id)}
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Timeline */}
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="w-5 h-5" />
                  Bitácora de Cambios
                </CardTitle>
              </CardHeader>
              <CardContent>
                {bitacora.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">Sin registros</p>
                ) : (
                  <div className="space-y-4 max-h-[600px] overflow-y-auto">
                    {bitacora.map((entry, index) => (
                      <div key={entry.id} className="relative pl-6 pb-4 border-l-2 border-gray-200 last:border-l-0">
                        <div className="absolute -left-2 top-0 w-4 h-4 rounded-full bg-indigo-500"></div>
                        <div className="text-xs text-gray-500 mb-1">
                          {formatDateTime(entry.fecha_hora)}
                        </div>
                        <div className="text-sm font-medium text-gray-900">
                          {entry.campo_modificado}
                        </div>
                        {entry.valor_anterior && entry.valor_nuevo && (
                          <div className="text-sm text-gray-600 mt-1">
                            <span className="text-red-500 line-through">{entry.valor_anterior}</span>
                            {" → "}
                            <span className="text-green-600">{entry.valor_nuevo}</span>
                          </div>
                        )}
                        {!entry.valor_anterior && entry.valor_nuevo && (
                          <div className="text-sm text-gray-600 mt-1">
                            {entry.valor_nuevo}
                          </div>
                        )}
                        {entry.valor_anterior && !entry.valor_nuevo && (
                          <div className="text-sm text-gray-600 mt-1">
                            {entry.valor_anterior}
                          </div>
                        )}
                        {entry.nota && (
                          <div className="text-sm text-gray-500 italic mt-1">
                            "{entry.nota}"
                          </div>
                        )}
                        <div className="text-xs text-gray-400 mt-1">
                          por {entry.usuario_nombre}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Add Link Modal */}
      <Dialog open={showLinkModal} onOpenChange={setShowLinkModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Agregar Link</DialogTitle>
            <DialogDescription>
              Agrega un enlace externo a esta renovación (ej: Google Drive, Dropbox)
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Nombre del link</Label>
              <Input
                value={newLink.nombre}
                onChange={(e) => setNewLink({...newLink, nombre: e.target.value})}
                placeholder="Ej: Contrato firmado en Drive"
              />
            </div>
            <div>
              <Label>URL</Label>
              <Input
                value={newLink.url}
                onChange={(e) => setNewLink({...newLink, url: e.target.value})}
                placeholder="https://..."
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLinkModal(false)}>
              Cancelar
            </Button>
            <Button onClick={handleAddLink}>
              Agregar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
